/*
 * Copyright (c) Kalaora 2022.
 * Author: fharold
 *
 * hilo-oauth-manager
 * NavigationService.ts
 */

import {Listener, Service} from "./Service";
import {History} from "history";
import {Page} from "../model/pagination/Page";

export interface NavigationListener extends Listener<History> {
    historyHasBeenUpdated(): void;
}

export default class NavigationService extends Service<History, NavigationListener> {
    private _history?: History = undefined;
    private _currentPage?: Page = undefined;
    private _currentPath?: string;
    public static id = "NavigationService";

    public get currentPage() {
        return this._currentPage
    }

    static pages: Array<Page> = [
        {
            icon: "/img/list.png",
            titleAccessor: t => t.library,
            paths: ["/categories", "/category/*"]
        },
        {
            icon: "/img/books.png",
            titleAccessor: t => t.catalogs,
            paths: ["/catalogs", "/catalog/*"]
        },{
            icon: "/img/users.png",
            titleAccessor: t => t.allUsers,
            paths: ["/profiles", "/profile/*"]
        },
    ]

    /**
     * pathname
     * @returns {undefined | Pathname}
     */
    public get pathname() {
        if (!this._history) {
            return undefined;
        }

        return this._history.location.pathname;
    }

    /**
     * searchParams
     * @returns {undefined | Map<string, string>}
     */
    public get searchParams() {
        if (!this._history) {
            return undefined;
        }

        const {search} = this._history.location;
        let params: Map<string, string> = new Map();

        if (!search) {
            return params;
        }

        const epurSearch = search.replace('?', '');
        const splittedParams = epurSearch.split('&');

        splittedParams.forEach(splittedParam => {
            const keyVal = splittedParam.split('=');
            params.set(keyVal[0], keyVal[1]);
        });

        return params;
    }

    /**
     * start
     */
    public start() {
        super.start();

        this._currentPage = NavigationService.pages.find(this.isGivenPageTheCurrentPage);
    }

    public isGivenPageTheCurrentPage = (page: Page) => {
        for (let path of page.paths) {
            if (this.isGivenPathTheCurrentPage(path)) {
                return true;
            }
        }

        return false;
    };

    /**
     * getPageByPath
     * @param needle
     */
    public getPageByPath = (needle: string): Page | undefined => {
        for (let page of NavigationService.pages) {
            for (let path of page.paths) {
                const indexOfWildCard = path.indexOf("/*");

                if (indexOfWildCard !== -1) {
                    const uriComponent = path.substr(0, indexOfWildCard);
                    //once you got the left part, strncmp to window current location to see if it matches
                    if (needle.substr(0, indexOfWildCard) === uriComponent) {
                        return page;
                    }
                } else if (path === needle) {
                    return page;
                }
            }
        }

        return undefined;
    }

    /**
     * isGivenPathTheCurrentPage
     * @param path
     * @returns {boolean}
     */
    public isGivenPathTheCurrentPage(path: string): boolean {
        return this.currentPage !== undefined && this.currentPage === this.getPageByPath(path);
    }

    /**
     * stop
     */
    public stop() {
    }

    /**
     * setHistory
     * @param val
     */
    public setHistory = (val: History) => {
        this._history = val;
        this._postLoading();
    };

    /**
     * _postLoading
     * @private
     */
    private _postLoading() {
        this._currentPath = this._history?.location.pathname;

        if (this._currentPath) this._currentPage = this.getPageByPath(this._currentPath);

        this.notifyListeners();
    }

    /**
     * load
     * @param pathname
     */
    public load(pathname: string) {
        if (!this._history) {
            return;
        }
        this._history.push(pathname);
        this._postLoading()
    }


  /**
     * goToParentPage
     */
    public goToParentPage = () => {
        if (!this._currentPath || !this.currentPage) return;

        this._history?.back();
    };

    /**
     * notifyListeners
     */
    public notifyListeners = async () => {
        this.listeners.forEach((l) => l.historyHasBeenUpdated())
    };

    /**
     * getIdFromPathname
     * @returns {string | undefined}
     * @param prefix
     */
    public getIdFromPathname = (prefix: string): string | undefined => {
        try {
            if (!this.pathname || (this.pathname && this.pathname.indexOf(prefix) < 0)) {
                return undefined;
            }

            const filteredPath = this.pathname.replace(prefix, "");
            const splittedPathname = filteredPath.split('/');

            if (splittedPathname && splittedPathname?.length > 1) {
                return splittedPathname[splittedPathname?.length - 1];
            }
        } catch (error) {
            console.log(error)
        }

        return undefined;
    };

    /**
     * goBack
     */
    public goBack() {
        if (!this._history) {
            return;
        }

        this._history.back();
        this.notifyListeners();
    }
}